/**
 * «سلامت حساب‌ها» — تراز آزمایشیِ حقوق (v2.173.0).
 *
 * کاربر (۱۴۰۵/۰۸/۱۲): «دوبرابر نشه، چیزی کم نباشه، چیزی زیاد شد مشخص بشه». سرور (`services/ledger_health.py`) کنترل‌ها را
 * می‌سنجد: انتقالِ بی‌جفت، پرداخت بدونِ سندِ برابر، حقوقِ ثبت‌شده ≠ خالصِ فیش، تسویهٔ تکراری، سندِ یک‌طرفه با اسمِ همکار،
 * سندِ بعد از بستن، تکراری، مبلغِ غیرعادی. فقط می‌خواند؛ اصلاح با سندِ اصلاحی در «اسناد مالی» یا «انتقال بین پرسنل».
 * راهنمای کامل: `docs/ACCOUNTING.md`.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Segmented, Space, Spin, Table, Tag, Typography } from "antd";
import { CheckCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import Alert from "../../components/GxAlert";
import GxTable from "../../components/GxTable";
import { getLedgerHealth } from "../../api/client";
import { formatMoney } from "../../utils/money";
import { byStr } from "../../utils/sorters";
import { apiError } from "../../utils/apiError";
import useRefreshOnShow from "../../utils/useRefreshOnShow";

const { Text } = Typography;

export default function LedgerHealthTab({ onOpenLedger, active }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [level, setLevel] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    getLedgerHealth().then((r) => { setData(r.data); setErr(null); })
      .catch((e) => setErr(apiError(e, "خوانده نشد"))).finally(() => setLoading(false));
  }, []);
  // اولین نمایش؛ بعد از آن هر بار که تب دیده شد: useRefreshOnShow
  useEffect(() => { if (active) load(); }, []);   // eslint-disable-line
  useRefreshOnShow(load, active);

  const rows = useMemo(() => (data?.issues || [])
    .filter((i) => level === "all" || i.level === level)
    .map((i, k) => ({ ...i, key: k })), [data, level]);

  const cols = [
    { title: "شدت", dataIndex: "level", width: 80,
      render: (v) => (v === "error" ? <Tag color="red">خطا</Tag> : <Tag color="orange">هشدار</Tag>) },
    { title: "نوع", dataIndex: "label", width: 220, sorter: byStr("label") },
    { title: "شخص", dataIndex: "employee_name", width: 180, sorter: byStr("employee_name"),
      render: (v, r) => (r.employee_id && onOpenLedger
        ? <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onOpenLedger(r.employee_id)}>{v}</Button>
        : v) },
    { title: "شرح", dataIndex: "text" },
  ];

  const s = data?.summary;
  return (
    <Card title="سلامت حساب‌ها — تراز آزمایشیِ حقوق"
      extra={<Button icon={<ReloadOutlined />} loading={loading} onClick={load}>بررسیِ دوباره</Button>}>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="هر بار که این صفحه را باز کنید، کلِ دفترِ حسابِ پرسنل با قاعده‌های حسابداری کنترل می‌شود."
        description={<span>
          <b>خطا</b> یعنی عدد احتمالاً غلط است (سندِ یک‌طرفه، انتقالِ بی‌جفت، حقوقِ دوبار ثبت‌شده). <b>هشدار</b> یعنی ببینید
          (مبلغِ غیرعادی، سندِ احتمالاً تکراری، سندی که بعد از بستنِ ماه به همان ماه زده شده). اصلاح همیشه با سندِ اصلاحی یا
          «انتقال بین پرسنل» است، نه دست‌بردن در سندِ ماهِ بسته.</span>} />
      {err && <Alert type="error" showIcon message={err} style={{ marginBottom: 12 }} />}
      {!data && loading && <Spin />}
      {data && (
        <>
          <Space wrap style={{ marginBottom: 12 }}>
            {s.errors === 0 && s.warnings === 0
              ? <Tag icon={<CheckCircleOutlined />} color="green">همه‌چیز می‌خواند</Tag>
              : <>
                <Tag color={s.errors ? "red" : "green"}>{s.errors} خطا</Tag>
                <Tag color={s.warnings ? "orange" : "green"}>{s.warnings} هشدار</Tag>
                {s.by_check.map((c) => <Tag key={c.check}>{c.label}: {c.count}</Tag>)}
              </>}
          </Space>
          <Text strong>کنترلِ ماه‌های بسته</Text>
          <Table size="small" pagination={false} rowKey="period" style={{ margin: "6px 0 16px" }}
            dataSource={data.controls}
            columns={[
              { title: "ماه", dataIndex: "period" },
              { title: "جمعِ خالصِ فیش‌ها", dataIndex: "net_total", render: (v) => formatMoney(v) },
              { title: "جمعِ حقوقِ ثبت‌شده در حساب‌ها", dataIndex: "posted_total", render: (v) => formatMoney(v) },
              { title: "جمعِ پرداختی‌ها", dataIndex: "paid_total", render: (v) => formatMoney(v) },
              { title: "تراز", dataIndex: "ok",
                render: (v, r) => (v ? <Tag color="green">برابر</Tag>
                  : <Tag color="red">اختلاف {formatMoney(r.net_total - r.posted_total)}</Tag>) },
            ]} />
          <Space style={{ marginBottom: 8 }}>
            <Segmented value={level} onChange={setLevel}
              options={[{ value: "all", label: "همه" }, { value: "error", label: "خطاها" }, { value: "warning", label: "هشدارها" }]} />
          </Space>
          <GxTable layoutId="ledger-health" dataSource={rows} columns={cols} rowKey="key" size="small"
            gxExportName="سلامت-حساب‌ها" scroll={{ x: "max-content" }} />
        </>
      )}
    </Card>
  );
}
